import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_URL, getMediaUrl } from '../../config';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Pencil, Video as VideoIcon, X, Search, Youtube, Instagram, Facebook, Upload } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_FORM = {
  title: '',
  video_url: '',
  description: '',
  project_id: '',
  thumbnail_url: ''
};

export default function VideosManager() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [activeFilter, setActiveFilter] = useState('All');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API_URL}/videos`);
      setVideos(res.data || []);
    } catch (err) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const openAddModal = () => {
    setEditId(null);
    setFormData(EMPTY_FORM);
    setIsModalOpen(true);
  };

  const openEditModal = (video) => {
    setEditId(video.id);
    setFormData({
      title: video.title || '',
      video_url: video.video_url || '',
      description: video.description || '',
      project_id: video.project_id || '',
      thumbnail_url: video.thumbnail_url || ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.video_url) {
      toast.error('Video URL is required');
      return;
    }
    try {
      if (editId) {
        await axios.put(`${API_URL}/videos/${editId}`, formData);
        toast.success('Video updated successfully!');
      } else {
        await axios.post(`${API_URL}/videos`, formData);
        toast.success('Video added successfully!');
      }
      setIsModalOpen(false);
      fetchVideos();
    } catch (err) {
      toast.error(editId ? 'Failed to update video' : 'Failed to add video');
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      await axios.delete(`${API_URL}/videos/${id}`);
      toast.success('Video deleted');
      fetchVideos();
    } catch (err) {
      toast.error('Failed to delete video');
    }
  };

  const getPlatformIcon = (platform) => {
    switch(platform?.toLowerCase()) {
      case 'youtube': return <Youtube size={16} className="text-red-500" />;
      case 'instagram': return <Instagram size={16} className="text-pink-500" />;
      case 'facebook': return <Facebook size={16} className="text-blue-500" />;
      default: return <VideoIcon size={16} className="text-neutral-400" />;
    }
  };

  const isDirectVideo = (url) => {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    return lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.webm') || lowerUrl.endsWith('.ogg') || lowerUrl.includes('/uploads/') || lowerUrl.includes('/backend/uploads/');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a valid video file.');
      return;
    }
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const res = await axios.post(`${API_URL}/upload/video`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });
      
      setFormData({
        ...formData,
        video_url: res.data.url,
        title: formData.title || file.name.split('.')[0]
      });
      toast.success('Video uploaded successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const getEmbedUrl = (url) => {
    if (!url) return null;
    if (isDirectVideo(url)) return getMediaUrl(url);
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1].split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('youtube.com/shorts/')) {
      const videoId = url.split('shorts/')[1].split('?')[0].split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1].split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('instagram.com/p/') || url.includes('instagram.com/reel/')) {
      const cleanUrl = url.split('?')[0].replace(/\/$/, '');
      return `${cleanUrl}/embed`;
    }
    return url;
  };

  const filteredVideos = useMemo(() => {
    if (activeFilter === 'All') return videos;
    return videos.filter(v => v.platform?.toLowerCase() === activeFilter.toLowerCase());
  }, [videos, activeFilter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Videos Manager</h2>
          <p className="text-neutral-400 text-sm">Manage standalone social media video embeds.</p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-amber-500 hover:bg-amber-600 text-black font-medium px-4 py-2 rounded-xl flex items-center gap-2 transition"
        >
          <Plus size={20} /> Add Video
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 border-b border-neutral-800 pb-4">
        {['All', 'YouTube', 'Instagram', 'Facebook'].map(filter => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
              activeFilter === filter
                ? 'bg-amber-500 text-black'
                : 'bg-neutral-900 text-neutral-400 hover:text-white hover:bg-neutral-800'
            }`}
          >
            {filter !== 'All' && getPlatformIcon(filter)}
            {filter}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredVideos.map((video) => (
              <motion.div
                layout
                key={video.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-neutral-900 border border-neutral-800 rounded-2xl overflow-hidden group cursor-pointer hover:border-neutral-700 transition"
                onClick={() => openEditModal(video)}
              >
                <div className="aspect-video bg-neutral-800 relative group">
                  {video.thumbnail_url ? (
                    <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-neutral-800 text-neutral-600 overflow-hidden">
                      {isDirectVideo(video.video_url) ? (
                        <video src={getMediaUrl(video.video_url)} className="w-full h-full object-cover" muted playsInline />
                      ) : (
                        getPlatformIcon(video.platform)
                      )}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex items-center gap-1">
                    <span className="bg-black/60 backdrop-blur text-xs font-semibold px-2 py-1 rounded-md flex items-center gap-1 border border-white/10">
                      {getPlatformIcon(video.platform)}
                      <span className="text-white/80 capitalize">{video.platform || 'Link'}</span>
                    </span>
                  </div>
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-sm">
                    <button className="bg-amber-500 text-black px-4 py-2 rounded-xl flex items-center gap-2 font-medium">
                      <Pencil size={18} /> Edit Video
                    </button>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-white mb-1 truncate">{video.title || "Untitled Video"}</h3>
                  <p className="text-sm text-neutral-400 line-clamp-2 mb-4">
                    {video.description || "No description provided."}
                  </p>
                  
                  <div className="flex items-center justify-between border-t border-neutral-800 pt-4">
                    {video.project_id ? (
                      <span className="text-xs text-neutral-500 bg-neutral-800 px-2 py-1 rounded">
                        Project: {video.project_id}
                      </span>
                    ) : (
                      <span className="text-xs text-neutral-500">Standalone</span>
                    )}
                    
                    <button
                      onClick={(e) => handleDelete(e, video.id)}
                      className="text-neutral-500 hover:text-red-500 transition p-1 hover:bg-red-500/10 rounded"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          {filteredVideos.length === 0 && (
             <div className="col-span-full py-16 text-center text-neutral-500 border border-dashed border-neutral-800 rounded-2xl flex flex-col items-center">
                 <VideoIcon size={48} className="mb-4 opacity-50" />
                 <p>No videos found. Add one to get started.</p>
             </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setIsModalOpen(false)}
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-neutral-900 border border-neutral-800 rounded-2xl p-6 w-full max-w-xl shadow-2xl overflow-y-auto max-h-[90vh]"
            >
              <button 
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 text-neutral-400 hover:text-white"
              >
                <X size={20} />
              </button>

              <h3 className="text-xl font-bold text-white mb-6">
                {editId ? 'Edit Video' : 'Add New Video'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Live Preview */}
                {formData.video_url && (
                  <div className="mb-4 rounded-xl overflow-hidden border border-neutral-800 aspect-video bg-black flex items-center justify-center">
                    {isDirectVideo(formData.video_url) ? (
                      <video
                        src={getMediaUrl(formData.video_url)}
                        controls
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <iframe
                        src={getEmbedUrl(formData.video_url)}
                        className="w-full h-full"
                        allowFullScreen
                        allow="autoplay; encrypted-media; picture-in-picture"
                        scrolling={formData.video_url.includes('instagram') ? "no" : "auto"}
                        frameBorder="0"
                      />
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-400 mb-1">Video URL *</label>
                    <input 
                      required 
                      type="text" 
                      value={formData.video_url} 
                      onChange={e => setFormData({...formData, video_url: e.target.value})} 
                      placeholder="https://youtube.com/watch?v=... or file path" 
                      className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-amber-500 transition text-sm" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-400 mb-1">Or Upload Video File</label>
                    <div className="relative">
                      <input 
                        type="file" 
                        accept="video/*" 
                        onChange={handleFileUpload} 
                        className="hidden" 
                        id="video-upload-input"
                        disabled={uploading}
                      />
                      <label 
                        htmlFor="video-upload-input"
                        className="w-full bg-neutral-950 hover:bg-neutral-800 border border-neutral-800 rounded-xl px-4 py-2.5 text-neutral-300 hover:text-white cursor-pointer transition flex items-center justify-center gap-2 text-sm border-dashed"
                      >
                        <Upload size={16} />
                        {uploading ? `Uploading ${uploadProgress}%` : 'Choose Video File'}
                      </label>
                    </div>
                  </div>
                </div>

                {uploading && (
                  <div className="w-full bg-neutral-800 h-1.5 rounded-full overflow-hidden">
                    <div 
                      className="bg-amber-500 h-full transition-all duration-300" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Title</label>
                  <input 
                    type="text" 
                    value={formData.title} 
                    onChange={e => setFormData({...formData, title: e.target.value})} 
                    className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-amber-500 transition" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Description</label>
                  <textarea 
                    rows={2} 
                    value={formData.description} 
                    onChange={e => setFormData({...formData, description: e.target.value})} 
                    className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-amber-500 transition"
                  ></textarea>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-400 mb-1">Thumbnail URL</label>
                    <input 
                      type="url" 
                      value={formData.thumbnail_url} 
                      onChange={e => setFormData({...formData, thumbnail_url: e.target.value})} 
                      placeholder="Optional image cover" 
                      className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-amber-500 transition" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-400 mb-1">Associated Project ID</label>
                    <input 
                      type="text" 
                      value={formData.project_id} 
                      onChange={e => setFormData({...formData, project_id: e.target.value})} 
                      placeholder="Optional" 
                      className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-amber-500 transition" 
                    />
                  </div>
                </div>
                
                <div className="pt-6 flex gap-3">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 px-4 py-3 rounded-xl border border-neutral-700 text-neutral-300 hover:bg-neutral-800 transition font-medium">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-3 rounded-xl bg-amber-500 text-black font-medium hover:bg-amber-600 transition shadow-[0_0_20px_rgba(245,158,11,0.2)]">
                    {editId ? 'Save Changes' : 'Add Video'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
